import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { ApiKeyData, getApiKeyData, isAdminRole, isRefereeRole } from '../utils/ApiKeyData';
import { getUserProfile, User } from '../services/UserService';

function encodeToBase64Unicode(str: string): string {
  return btoa(unescape(encodeURIComponent(str)));
}

function decodeFromBase64Unicode(str: string): string {
  return decodeURIComponent(escape(atob(str)));
}

const API_KEY_STORE = 'apiKey';
const PROFILE_STORE = 'profile';

interface ApiKeyContextType {
  apiKey: string;
  saveApiKey: (key: string) => void;
  apiKeyData?: ApiKeyData;
  userProfile?: User;
  isAdmin?: boolean;
  isReferee?: boolean;
  clearApiKey: () => void;
}

const ApiKeyContext = createContext<ApiKeyContextType | undefined>(undefined);

export const useApiKey = (): ApiKeyContextType => {
  const context = useContext(ApiKeyContext);
  if (!context) {
    throw new Error('useApiKey must be used within an ApiKeyProvider');
  }
  return context;
};

interface ApiKeyProviderProps {
  children: ReactNode;
}

export const ApiKeyProvider: React.FC<ApiKeyProviderProps> = ({ children }) => {
  const [apiKey, setApiKey] = useState<string>(() => {
    return localStorage.getItem(API_KEY_STORE) || '';
  });
  const [userProfile, setUserProfile] = useState<User | undefined>(() => {
    try {
      const user: User = JSON.parse(decodeFromBase64Unicode(localStorage.getItem(PROFILE_STORE) ?? ''));
      return user;
    } catch {
      return undefined;
    }
  });
  const [apiKeyData, setApiKeyData] = useState<ApiKeyData>();
  const [isAdmin, setIsAdmin] = useState<boolean>();
  const [isReferee, setIsReferee] = useState<boolean>();

  const saveApiKey = (key: string) => {
    setApiKey(key);
    localStorage.setItem(API_KEY_STORE, key);
  };

  useEffect(() => {
    if (apiKey !== '') {
      setApiKeyData(getApiKeyData(apiKey));
      if (userProfile === undefined) {
        var profile = localStorage.getItem(PROFILE_STORE);
        if (profile === null)
          getUserProfile(apiKey).then((user) => {
            setUserProfile(user);
            localStorage.setItem(PROFILE_STORE, encodeToBase64Unicode(JSON.stringify(user)));
          });
      }
    }
  }, [apiKey]);

  useEffect(() => {
    if (apiKeyData) {
      setIsAdmin(isAdminRole(apiKeyData));
      setIsReferee(isRefereeRole(apiKeyData));
    }
  }, [apiKeyData]);

  const clearApiKey = () => {
    setApiKey('');
    localStorage.removeItem(API_KEY_STORE);
    localStorage.removeItem(PROFILE_STORE);
    setApiKeyData(undefined);
    setUserProfile(undefined);
    setIsAdmin(undefined);
    setIsReferee(undefined);
  };

  return (
    <ApiKeyContext.Provider value={{ apiKey, saveApiKey, userProfile, apiKeyData, isAdmin, isReferee, clearApiKey }}>
      {children}
    </ApiKeyContext.Provider>
  );
};
